import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Alert, Badge, Card, NotProvided, Page, PageHeader, Table } from '@evogard/ui';
import { formatInstant, loadFailureMessage } from '@evogard/domain';
import { useTablePage } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * Os tipos de boleto do SGA, e quantos boletos da praça são de cada um
 * (14/10/2026).
 *
 * A lista vem de `GET /listar/tipo-boleto/todos` pela esteira de referência; a
 * contagem, do cadastro de boletos da casa. Tipo que algum boleto usa e a lista
 * não tem aparece também, com o nome que o próprio boleto trouxe — sumir com
 * ele seria a tela escondendo boleto.
 */
export function BoletoTypesScreen() {
  const { data: context } = useSessionContext();
  /* a página na URL — o "voltar" devolve a pessoa onde ela estava */
  const pages = useSearch({ from: '/authenticated/erp/tipos-boleto' });
  const page = useTablePage('pagina', pages.pagina);
  const tenantId = context?.currentTenant.id;

  const query = useQuery({
    queryKey: ['erp', 'tipos-boleto', tenantId],
    queryFn: async () => {
      const r = await api.erp.boletoTypes();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os tipos de boleto'));
      return r.body;
    },
  });
  const result = query.data;

  return (
    <Page measure="cheia">
      <PageHeader title="Tipos de boleto">
        Os tipos que o ERP de {context?.currentTenant.nome} usa, e quantos boletos cada um
        tem no cadastro.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={
            result?.estado === 'lida' ? `${result.items.length} tipo(s)` : 'Carregando…'
          }
          descricao={
            result?.estado === 'lida' && result.syncedAt
              ? `lista do ERP sincronizada em ${formatInstant(result.syncedAt)}`
              : 'a lista do ERP ainda não foi sincronizada — os tipos abaixo vêm dos boletos'
          }
          bleed
        >
          <Table
            {...page}
            label="Tipos de boleto"
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            rowKey={(t) => t.code}
            empty="Nenhum tipo de boleto nesta base."
            columns={[
              {
                title: 'Código',
                minWidth: '6rem',
                cell: (t) => <span className="font-mono">{t.code}</span>,
              },
              {
                title: 'Tipo',
                alignment: 'inicio' as const,
                minWidth: '16rem',
                cell: (t) => (
                  <span className="flex items-center gap-2">
                    {t.label ?? <NotProvided>sem nome no ERP</NotProvided>}
                    {t.adhesion && <Badge tone="informacao">adesão</Badge>}
                  </span>
                ),
              },
              {
                title: 'Na lista',
                minWidth: '8rem',
                /* a lista de tipos do SGA não traz situação: "na lista" é o que se sabe */
                cell: (t) =>
                  t.inList ? (
                    (t.situacao ?? 'na lista do ERP')
                  ) : (
                    <NotProvided>fora da lista do ERP</NotProvided>
                  ),
              },
              {
                title: 'Boletos',
                minWidth: '7rem',
                cell: (t) => (
                  <span className="tabular-nums">
                    {t.boletos.toLocaleString('pt-BR')}
                  </span>
                ),
              },
              {
                title: 'Pagos, pelo ERP',
                minWidth: '8rem',
                cell: (t) => (
                  <span className="tabular-nums">{t.paid.toLocaleString('pt-BR')}</span>
                ),
              },
            ]}
          />
        </Card>
      )}
    </Page>
  );
}
